import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LoadingSpinner, PageLoadingSpinner } from '@/components/ui/loading-spinner';
import { MobileBottomNav } from '@/components/ui/mobile-bottom-nav';
import { AppDesktopSidebar } from '@/components/ui/app-desktop-sidebar';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { AuthProvider } from '@/providers/AuthProvider';
import { WebAccessProvider, useWebAccess } from '@/providers/WebAccessProvider';
import { useAuth } from '@/hooks';
import { AuthStateChangeHandler, ProtectedRoute } from '@/components/auth-guard';
import { ThemeProvider } from './components/theme-provider';
import { PageTransition } from './components/page-transition';
import { ErrorBoundary, RouteErrorBoundary } from './components/error-boundary';
import { ChatProvider, useChatContext } from './contexts/ChatContext';
import { FocusModeProvider } from './contexts/FocusModeContext';
import { NotificationQueueProvider } from './contexts/NotificationQueueContext';
import { CommandMenuLoader } from '@/components/ui/command-menu-loader';
import { NativeAppUrlListener } from '@/components/native-app-url-listener';
import { NativeSmokeRouteListener } from '@/components/native-smoke-route-listener';
import { MotionProvider } from '@/components/motion-provider';
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { initializeConsentedSentry } from './lib/sentry-loader';
import { SwipeableTabsContainer } from '@/components/swipeable-tabs-container';
import { InteractiveBackGestureLoader } from '@/components/interactive-back-gesture-loader';
import { prewarmAudio } from '@/lib/sound-engine';
import { AppBackdrop } from '@/components/ui/page-shell';
import { CookieConsentBanner } from '@/components/consent/CookieConsentBanner';
import { isEmbeddedNativeShell } from '@/lib/embedded-native-shell';
import { cn } from '@/lib/utils';

// Lazy load heavy components
const LandingPage = lazy(() => import('./pages/LandingPage'));
const OnboardingWelcome = lazy(() => import('./pages/OnboardingWelcome'));
const Auth = lazy(() => import('./pages/Auth'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Rewards = lazy(() => import('./pages/Rewards'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Coach = lazy(() => import('./pages/Coach'));
const Performance = lazy(() => import('./pages/Performance'));
const CompanyAdmin = lazy(() => import('./pages/CompanyAdmin'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Imprint = lazy(() => import('./pages/Imprint'));
const OpenSourceLicenses = lazy(() => import('./pages/OpenSourceLicenses'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const HealthConsent = lazy(() => import('./pages/HealthConsent'));
const Anamnesebogen = lazy(() => import('./pages/Anamnesebogen'));
const FitbitCallback = lazy(() => import('./pages/FitbitCallback'));
const GoogleHealthCallback = lazy(() => import('./pages/GoogleHealthCallback'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Admin = lazy(() => import('./pages/Admin').then((module) => ({ default: module.default })));
const AdminRBAC = lazy(() =>
  import('./pages/AdminRBAC').then((module) => ({ default: module.default })),
);
const I18nDebug = lazy(() => import('./pages/I18nDebug'));
const AnamnesebogenDirect = lazy(() => import('./pages/AnamnesebogenDirect'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Status = lazy(() => import('./pages/Status'));
const JoinGroup = lazy(() => import('./pages/JoinGroup'));
const HealthCoachChat = lazy(() =>
  import('@/components/Dashboard/health-coach-chat').then((module) => ({
    default: module.HealthCoachChat,
  })),
);

const AUTHED_APP_ROUTE_PREFIXES = [
  '/dashboard',
  '/profile',
  '/coach',
  '/performance',
  '/rewards',
  '/leaderboard',
  '/company-admin',
] as const;
const DESKTOP_SHELL_ROUTE_PREFIXES = [...AUTHED_APP_ROUTE_PREFIXES, '/admin'] as const;
const DESKTOP_SIDEBAR_STORAGE_KEY = 'vital_desktop_sidebar_collapsed';

const isAuthedAppRoute = (pathname: string) =>
  AUTHED_APP_ROUTE_PREFIXES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
const isDesktopShellRoute = (pathname: string) =>
  DESKTOP_SHELL_ROUTE_PREFIXES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );

function focusMainContent() {
  window.requestAnimationFrame(() => {
    document.getElementById('main-content')?.focus({ preventScroll: true });
  });
}

const DeferredHealthCoachChat = () => {
  const { isOpen } = useChatContext();
  const { t } = useTranslation();
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldMount(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (shouldMount) return;

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(() => setShouldMount(true), { timeout: 4000 });
      return () => idleWindow.cancelIdleCallback?.(handle);
    }

    const timeout = window.setTimeout(() => setShouldMount(true), 2500);
    return () => window.clearTimeout(timeout);
  }, [shouldMount]);

  if (!shouldMount) return null;

  return (
    <Suspense
      fallback={
        <div
          className={cn(
            'fixed inset-0 z-50 items-center justify-center bg-background/85 backdrop-blur-sm',
            isOpen ? 'flex' : 'hidden',
          )}
          role="status"
          aria-live="polite"
        >
          <LoadingSpinner size="lg" text={t('common.loading', 'Loading...')} />
        </div>
      }
    >
      <HealthCoachChat />
    </Suspense>
  );
};

// Monitoring is loaded only for users who already granted analytics consent.
void initializeConsentedSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - keep prefetched data fresh longer
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Scroll restoration component - ensures 100% of page transitions start at top
const ScrollToTop = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    const resetElementScroll = (element: HTMLElement | null) => {
      if (!element) return;

      if (typeof element.scrollTo === 'function') {
        element.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        return;
      }

      // JSDOM and older embedded WebViews may not implement Element.scrollTo.
      element.scrollTop = 0;
      element.scrollLeft = 0;
    };

    const resetScroll = () => {
      resetElementScroll(document.getElementById('main-content'));
      if (typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }

      document.querySelectorAll('[data-cached-route]').forEach((element) => {
        if (element instanceof HTMLElement) {
          resetElementScroll(element);
        }
      });
    };

    // Run once in the current commit and once after lazy route content has mounted.
    resetScroll();
    const frame = requestAnimationFrame(resetScroll);

    return () => cancelAnimationFrame(frame);
  }, [location.pathname]);

  return null;
};

// Keep <html lang> in sync with i18n language
const LangSync = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language || 'en';
  }, [i18n.language]);
  return null;
};

const AppShell = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const { setIsOpen: setChatOpen } = useChatContext();
  const { healthReady } = useWebAccess();
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(DESKTOP_SIDEBAR_STORAGE_KEY) === 'true';
  });

  const shouldShowDesktopSidebar =
    !loading && Boolean(user) && !isEmbeddedNativeShell() && isDesktopShellRoute(location.pathname);
  const shouldRenderCoachChat = healthReady && isAuthedAppRoute(location.pathname);

  useEffect(() => {
    window.localStorage.setItem(
      DESKTOP_SIDEBAR_STORAGE_KEY,
      desktopSidebarCollapsed ? 'true' : 'false',
    );
  }, [desktopSidebarCollapsed]);

  useEffect(() => {
    if (!shouldRenderCoachChat) {
      setChatOpen(false);
    }
  }, [setChatOpen, shouldRenderCoachChat]);

  return (
    <div className="flex h-[100dvh] min-h-[100dvh] flex-col overflow-hidden bg-background pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
      <a
        href="#main-content"
        onClick={focusMainContent}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-background focus:px-4 focus:py-2 focus:text-body focus:font-semibold focus:text-foreground focus:shadow-lg focus:ring-2 focus:ring-ring"
      >
        {t('landing.skipToContent', 'Skip to content')}
      </a>
      {/* Fixed gradient backdrop – rendered once, outside all transform contexts */}
      <AppBackdrop />
      {shouldShowDesktopSidebar ? (
        <AppDesktopSidebar
          collapsed={desktopSidebarCollapsed}
          onCollapsedChange={setDesktopSidebarCollapsed}
        />
      ) : null}
      {/* iOS-style edge swipe is loaded only on coarse-pointer mobile devices. */}
      <InteractiveBackGestureLoader>
        <SwipeableTabsContainer>
          <main
            id="main-content"
            tabIndex={-1}
            className={cn(
              'relative z-0 h-full flex-1 overflow-y-auto overscroll-y-none outline-none transition-[padding] duration-200',
              shouldShowDesktopSidebar &&
                (desktopSidebarCollapsed
                  ? 'app-main-content-sidebar-collapsed'
                  : 'app-main-content-sidebar-expanded'),
            )}
          >
            {/* AppRoutes is the single route renderer. */}
            <AppRoutes />
          </main>
        </SwipeableTabsContainer>
      </InteractiveBackGestureLoader>

      {/* Chat and Nav sit above the z-0 content */}
      {shouldRenderCoachChat ? <DeferredHealthCoachChat /> : null}

      {/* z-50 prevents animation transforms from covering the navbar */}
      <div className="relative z-50">
        <MobileBottomNav />
      </div>

      {/* GDPR: consent banner rendered above backdrop, below nav */}
      <CookieConsentBanner />
    </div>
  );
};

/**
 * AppRoutes - single source of truth for route rendering.
 */
const AppRoutes = () => {
  return (
    <ErrorBoundary>
      <PageTransition>
        <Suspense fallback={<PageLoadingSpinner />}>
          <Routes>
            <Route
              path="/"
              element={
                <RouteErrorBoundary routeName="OnboardingWelcome">
                  <OnboardingWelcome />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/landing"
              element={
                <RouteErrorBoundary routeName="Landing">
                  <LandingPage />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/auth"
              element={
                <RouteErrorBoundary routeName="Auth">
                  <Auth />
                </RouteErrorBoundary>
              }
            />
            {/* Desktop fallback for cached routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Dashboard">
                    <Dashboard />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Profile">
                    <Profile />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-admin"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="CompanyAdmin">
                    <CompanyAdmin />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rewards"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Rewards">
                    <Rewards />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Leaderboard">
                    <Leaderboard />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Coach">
                    <Coach />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/performance"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Performance">
                    <Performance />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            {/* Non-cached routes */}
            <Route
              path="/privacy-policy"
              element={
                <RouteErrorBoundary routeName="PrivacyPolicy">
                  <PrivacyPolicy />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/privacy"
              element={
                <RouteErrorBoundary routeName="PrivacyPolicy">
                  <PrivacyPolicy />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/terms-of-service"
              element={
                <RouteErrorBoundary routeName="TermsOfService">
                  <TermsOfService />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/terms"
              element={
                <RouteErrorBoundary routeName="TermsOfService">
                  <TermsOfService />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/cookie-policy"
              element={
                <RouteErrorBoundary routeName="CookiePolicy">
                  <CookiePolicy />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/imprint"
              element={
                <RouteErrorBoundary routeName="Imprint">
                  <Imprint />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/open-source-licenses"
              element={
                <RouteErrorBoundary routeName="OpenSourceLicenses">
                  <OpenSourceLicenses />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/legal/open-source-licenses"
              element={
                <RouteErrorBoundary routeName="OpenSourceLicenses">
                  <OpenSourceLicenses />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/reset-password"
              element={
                <RouteErrorBoundary routeName="ResetPassword">
                  <ResetPassword />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/health-consent"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="HealthConsent">
                    <HealthConsent />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/anamnesebogen"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Anamnesebogen">
                    <Anamnesebogen />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Onboarding">
                    <Onboarding />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/auth/fitbit/callback"
              element={
                <RouteErrorBoundary routeName="FitbitCallback">
                  <FitbitCallback />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/auth/google-health/callback"
              element={
                <RouteErrorBoundary routeName="GoogleHealthCallback">
                  <GoogleHealthCallback />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="Admin">
                    <Admin />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rbac"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="AdminRBAC">
                    <AdminRBAC />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route path="/admin/login" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
            <Route
              path="/i18n-debug"
              element={
                <RouteErrorBoundary routeName="I18nDebug">
                  <I18nDebug />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/join/:groupId"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="JoinGroup">
                    <JoinGroup />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/anamnesebogen-direct"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary routeName="AnamnesebogenDirect">
                    <AnamnesebogenDirect />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/status"
              element={
                <RouteErrorBoundary routeName="Status">
                  <Status />
                </RouteErrorBoundary>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route
              path="*"
              element={
                <RouteErrorBoundary routeName="NotFound">
                  <NotFound />
                </RouteErrorBoundary>
              }
            />
          </Routes>
        </Suspense>
      </PageTransition>
    </ErrorBoundary>
  );
};

const App = () => {
  // Prewarm audio context on first user interaction (browser autoplay policy)
  useEffect(() => {
    const handleInteraction = () => {
      prewarmAudio();
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };

    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="vital-theme-v2">
        <MotionProvider>
          <TooltipProvider>
            <Toaster />
            <LangSync />
            <BrowserRouter>
              <ScrollToTop />
              <NativeAppUrlListener />
              <AuthProvider>
                <WebAccessProvider>
                <AuthStateChangeHandler />
                <NativeSmokeRouteListener />
                <ChatProvider>
                  <FocusModeProvider>
                    <NotificationQueueProvider>
                      <AppShell />
                      <CommandMenuLoader />
                    </NotificationQueueProvider>
                  </FocusModeProvider>
                </ChatProvider>
                </WebAccessProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </MotionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
